import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import logger from '../utils/logger';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check for existing session on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        // Verify token with backend
        try {
            const data = await api.auth.profile();
            const userData = data.data || data.user;
            
            // SECURITY: Verify the token matches the user data
            // This prevents session mixing when multiple users share a browser
            if (userData && userData.email) {
              // Check if the email matches what we stored during login
              const lastAuthEmail = localStorage.getItem('lastAuthEmail');
              if (lastAuthEmail && lastAuthEmail !== userData.email) {
                logger.error('Session mismatch detected! Stored email:', lastAuthEmail, 'Profile email:', userData.email);
                // Clear everything and force re-login
                localStorage.removeItem('authToken');
                localStorage.removeItem('lastAuthEmail');
                setUser(null);
                setIsAuthenticated(false);
                toast.error('Session mismatch detected. Please log in again.');
              } else {
                setUser(userData);
                setIsAuthenticated(true);
                // Update stored email if it wasn't set
                if (!lastAuthEmail) {
                  localStorage.setItem('lastAuthEmail', userData.email);
                }
              }
            } else {
              // Invalid response, clear authentication
              logger.error('Invalid user data received from profile endpoint');
              localStorage.removeItem('authToken');
              localStorage.removeItem('lastAuthEmail');
              setUser(null);
              setIsAuthenticated(false);
            }
          } catch (error) {
            // Token invalid, clear it
            logger.error('Token validation failed:', error);
            localStorage.removeItem('authToken');
          setUser(null);
          setIsAuthenticated(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      logger.error('Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password, rememberMe = false) => {
    // SECURITY: Clear any existing session data to prevent mixing
    localStorage.removeItem('authToken');
    localStorage.removeItem('lastAuthEmail');
    setUser(null);
    setIsAuthenticated(false);
    
    try {
      const data = await api.auth.login(email, password, rememberMe);
      const { user: userData, token } = data.data || data;
      
      // Store auth token and email for verification
      localStorage.setItem('authToken', token);
      localStorage.setItem('lastAuthEmail', userData.email);
      
      // Update state
      setUser(userData);
      setIsAuthenticated(true);
      
      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      logger.error('Login error:', error);
      
      // Check for specific error messages
      const errorMessage = error.message || 'Login failed. Please try again.';
      
      // Handle email verification error
      if (errorMessage.includes('verify your email')) {
        toast.error(errorMessage);
        return { success: false, error: errorMessage, needsVerification: true, email };
      }
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const register = async (email, password, firstName, lastName) => {
    try {
      const response = await api.auth.register(email, password, firstName, lastName);
      const message = response.data?.message || response.message || 'Registration successful! Please check your email to verify your account.';
      toast.success(message);
      return { success: true, message, needsVerification: true };
    } catch (error) {
      logger.error('Registration error:', error);
      toast.error(error.message || 'Registration failed. Please try again.');
      return { success: false, error: error.message || 'Network error' };
    }
  };

  const logout = () => {
    // SECURITY: Clear all auth-related data
    localStorage.removeItem('authToken');
    localStorage.removeItem('lastAuthEmail');
    setUser(null);
    setIsAuthenticated(false);
    toast.info('Logged out successfully');
  };

  const updateUserData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    const authToken = localStorage.getItem('authToken');
    
    // Skip update for mock admin
    if (authToken === 'mock-admin-token') {
      return;
    }
    
    // Prevent concurrent updates
    if (updateUserData._isUpdating) {
      return;
    }
    
    try {
      updateUserData._isUpdating = true;
      const data = await api.auth.profile();
      setUser(data.data || data.user);
    } catch (error) {
      logger.error('Failed to update user data:', error);
    } finally {
      updateUserData._isUpdating = false;
    }
  }, [isAuthenticated]);

  const value = {
    user,
    isLoading,
    isAuthenticated,
    login,
    register,
    logout,
    updateUserData,
    checkAuthStatus
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};